import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Tag, MessageCircle, ShoppingBag, ShieldCheck, Zap, ArrowRight, Check } from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "https://static.prod-images.emergentagent.com/jobs/0a9a82b6-40c7-4e8a-bd5d-f994eb0e1bfe/images/6c55d830accb3537e1cdf9d1650095ef87a9952556b54d852e9624aa6c0bba2e.png";

// Apple App Store listing URL for ISO Market
const APP_STORE_URL = "https://apps.apple.com/us/app/iso-market/id6796006808";

const AppleIcon = (props) => (
  <svg viewBox="0 0 384 512" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayIcon = (props) => (
  <svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const steps = [
  {
    icon: Tag,
    title: "Post your ISO",
    body: "List the exact item you're in search of \u2014 describe it, set your budget, and let the market know you're looking.",
  },
  {
    icon: Search,
    title: "Sellers find you",
    body: "People who have what you want reach out directly. No endless scrolling through listings you don't need.",
  },
  {
    icon: ShoppingBag,
    title: "Buy it, done",
    body: "Connect, agree, and complete the purchase. Straightforward buying and selling \u2014 no trading, no clutter.",
  },
];

const features = [
  { icon: MessageCircle, title: "Direct connections", body: "Buyers and sellers talk one-on-one, no middle layer." },
  { icon: Zap, title: "Demand-first", body: "Flip the marketplace \u2014 shoppers post, sellers come to them." },
  { icon: ShieldCheck, title: "Focused & simple", body: "Buy and sell only. A clean, honest experience by design." },
];

const Landing = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/notify`, { email });
      setJoined(true);
      toast.success("You're on the list! We'll be in touch. \ud83c\udf89");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      if (err?.response?.status === 409) {
        setJoined(true);
        toast.info(msg);
      } else {
        toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen iso-pattern overflow-x-hidden" data-testid="landing-page">
      <Toaster position="top-center" richColors />

      {/* Nav */}
      <nav className="w-full px-6 md:px-12 py-6 flex items-center justify-between max-w-7xl mx-auto" data-testid="navbar">
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <img src={LOGO} alt="ISO Market" className="h-10 w-10 object-contain rounded-xl" />
          <span className="font-display text-2xl font-bold tracking-tight text-[#1B1B1B]">
            ISO <span className="text-[#C65A3A]">Market</span>
          </span>
        </div>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5C6B4A] bg-[#5C6B4A]/10 border border-[#5C6B4A]/20 px-4 py-2 rounded-full hover:bg-[#5C6B4A]/20 transition-colors"
          data-testid="nav-ios-live-pill"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#5C6B4A] animate-pulse" /> Now Live on iOS
        </a>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-20 pb-20 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.span
            initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex sm:hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#5C6B4A] bg-[#5C6B4A]/10 border border-[#5C6B4A]/20 px-4 py-2 rounded-full mb-6"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#5C6B4A] animate-pulse" /> Now Live on iOS
          </motion.span>

          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-[#1B1B1B]"
            data-testid="hero-title"
          >
            Find what you're<br />
            <span className="text-[#C65A3A]">searching for.</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-6 text-base md:text-lg text-[#1B1B1B]/70 max-w-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            ISO Market flips the marketplace. Post the items you're <span className="italic">in search of</span>,
            and sellers who have them reach out to you. Buy and sell, direct and simple.
          </motion.p>

          {/* Store buttons */}
          <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-9 flex flex-col sm:flex-row gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-2xl bg-[#1B1B1B] px-6 py-3.5 text-white transition-all hover:bg-black shadow-lg shadow-[#1B1B1B]/15"
              data-testid="app-store-button"
            >
              <AppleIcon className="h-7 w-7" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] uppercase tracking-wide text-white/60">Download on the</span>
                <span className="block text-lg font-semibold -mt-0.5">App Store</span>
              </span>
            </a>
            <div
              className="inline-flex items-center gap-3 rounded-2xl bg-white border border-[#1B1B1B]/12 px-6 py-3.5 text-[#1B1B1B]/50 cursor-not-allowed select-none"
              data-testid="google-play-button"
              aria-disabled="true"
            >
              <PlayIcon className="h-6 w-6" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] uppercase tracking-wide text-[#C65A3A] font-semibold">Coming soon</span>
                <span className="block text-lg font-semibold -mt-0.5">Google Play</span>
              </span>
            </div>
          </motion.div>

          {/* Android notify */}
          <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="mt-8 max-w-md">
            <p className="text-sm font-semibold text-[#1B1B1B] mb-3">On Android? Get notified when we launch on Google Play.</p>
            {joined ? (
              <div
                className="flex items-center gap-3 bg-[#5C6B4A]/12 border border-[#5C6B4A]/25 text-[#3f4a33] rounded-2xl px-5 py-4"
                data-testid="joined-message"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#5C6B4A] text-white shrink-0">
                  <Check size={18} />
                </span>
                <p className="text-sm font-medium">You're on the list. We'll email you the moment ISO Market hits Android.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="notify-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 rounded-full border border-[#1B1B1B]/15 bg-white px-5 py-3.5 text-sm text-[#1B1B1B] placeholder:text-[#1B1B1B]/40 outline-none focus:border-[#C65A3A] focus:ring-2 focus:ring-[#C65A3A]/20 transition"
                  data-testid="email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C65A3A] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#a8482d] disabled:opacity-60 shadow-lg shadow-[#C65A3A]/20"
                  data-testid="notify-submit"
                >
                  {loading ? "Joining..." : "Notify me"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
            <p className="mt-3 text-xs text-[#1B1B1B]/45">No spam, ever. Just a heads-up when Android is ready.</p>
          </motion.div>
        </div>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-[#DCA24A]/20 blur-3xl rounded-full" />
          <div className="relative flex items-center justify-center h-72 w-72 md:h-96 md:w-96 rounded-full border border-[#1B1B1B]/10 bg-[#F7F4EF] shadow-2xl shadow-[#1B1B1B]/5">
            <div className="absolute h-56 w-56 md:h-72 md:w-72 rounded-full border border-dashed border-[#DCA24A]/50" />
            <img src={LOGO} alt="ISO Market logo" className="iso-float h-44 w-44 md:h-60 md:w-60 object-contain rounded-3xl drop-shadow-xl" data-testid="hero-logo" />
          </div>
        </motion.div>
      </header>

      {/* How it works */}
      <section className="bg-[#1B1B1B] text-[#F7F4EF] py-24" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#DCA24A] mb-4">How it works</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              Stop hunting. Start posting.
            </h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} custom={i} variants={fadeUp}
                className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-8 hover:bg-white/[0.06] transition-colors"
                data-testid={`step-card-${i}`}
              >
                <span className="font-display text-5xl font-black text-white/10 absolute top-6 right-7">0{i + 1}</span>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C65A3A] text-white mb-6">
                  <s.icon size={22} />
                </span>
                <h3 className="text-lg font-semibold mb-3">{s.title}</h3>
                <p className="text-sm text-[#F7F4EF]/60 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="features">
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} custom={i} variants={fadeUp}
              className="rounded-3xl bg-white border border-[#1B1B1B]/8 p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
              data-testid={`feature-card-${i}`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5C6B4A]/12 text-[#5C6B4A] mb-6">
                <f.icon size={22} />
              </span>
              <h3 className="font-display text-xl font-bold mb-2 text-[#1B1B1B]">{f.title}</h3>
              <p className="text-sm text-[#1B1B1B]/60 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="rounded-[2rem] bg-[#C65A3A] px-8 md:px-16 py-16 text-center relative overflow-hidden" data-testid="cta-band">
          <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10" />
          <div className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-[#DCA24A]/20" />
          <h2 className="relative font-display text-3xl md:text-4xl font-bold text-white">ISO Market is here.</h2>
          <p className="relative mt-4 text-white/85 max-w-xl mx-auto">
            Now live on the App Store for iPhone. Post what you're searching for and let sellers come to you.
            Android is on the way &mdash; Google Play coming soon.
          </p>
          <div className="relative mt-8 flex flex-col sm:flex-row justify-center items-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-2xl bg-[#1B1B1B] px-6 py-3.5 text-white hover:bg-black transition-colors"
              data-testid="cta-app-store-button"
            >
              <AppleIcon className="h-7 w-7" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] uppercase tracking-wide text-white/60">Download on the</span>
                <span className="block text-lg font-semibold -mt-0.5">App Store</span>
              </span>
            </a>
            <div className="inline-flex items-center gap-3 rounded-2xl bg-white/10 border border-white/20 px-6 py-3.5 text-white/60 cursor-not-allowed select-none" data-testid="cta-google-play-button">
              <PlayIcon className="h-6 w-6" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] uppercase tracking-wide text-[#DCA24A] font-semibold">Coming soon</span>
                <span className="block text-lg font-semibold -mt-0.5">Google Play</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1B1B1B]/10 py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="ISO Market" className="h-7 w-7 object-contain rounded-lg" />
            <span className="font-display font-bold text-[#1B1B1B]">ISO Market</span>
          </div>
          <p className="text-sm text-[#1B1B1B]/50">ISO-Market.com &middot; &copy; {new Date().getFullYear()} All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-sm text-[#1B1B1B]/60 hover:text-[#C65A3A] transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <p className="text-xs uppercase tracking-[0.18em] text-[#C65A3A] font-semibold">Buy &middot; Sell</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
